import FormBox from "./FormBox";
import type { ToolApprovalDecision, ToolApprovalRequest } from "@globals";
import { Button, Code, Group, Stack, Text, Textarea } from "@mantine/core";
import { useState } from "react";

type ToolApprovalCardProps = {
  requests: ToolApprovalRequest[];
  onRespond: (decisions: ToolApprovalDecision[]) => void;
};

/** Labels and display order for the deep-research payload, most important first. */
const RESEARCH_FIELD_LABELS: Record<string, string> = {
  query: "Research question",
  focus_areas: "Focus areas",
  constraints: "Constraints"
};

function formatValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(item => String(item)).join("\n");
  }
  if (value === null || value === undefined) {
    return "";
  }
  return typeof value === "string" ? value : JSON.stringify(value, null, 2);
}

/**
 * Arguments that route the call rather than travel in it. Showing them asks the user to vet an
 * identifier they did not write and cannot judge, which buries the text that actually leaves.
 *
 * A denylist, not an allowlist: an argument this file has not been taught about is still shown,
 * so a field added to the tool later cannot be approved unseen.
 */
const INTERNAL_FIELDS = new Set(["experiment_id"]);

type DisplayField = { key: string; label: string; text: string };

/** The fields to render, known ones first so the question always leads, empties dropped. */
function displayFields(input: Record<string, unknown>): DisplayField[] {
  const shown = Object.keys(input).filter(key => !INTERNAL_FIELDS.has(key));
  const known = Object.keys(RESEARCH_FIELD_LABELS).filter(key => shown.includes(key));
  const rest = shown.filter(key => !(key in RESEARCH_FIELD_LABELS));
  return [...known, ...rest]
    .map(key => ({ key, label: RESEARCH_FIELD_LABELS[key] ?? key, text: formatValue(input[key]) }))
    .filter(field => field.text !== "");
}

/**
 * Approval card for a gated tool call.
 *
 * Everything the tool will send is rendered in full and up front, because that payload is the
 * object of the approval — it leaves the platform verbatim if approved, so hiding it behind a
 * disclosure would defeat the point of asking. Rejecting reveals a feedback box; the note reaches
 * the agent as a tool error so it can revise and offer again.
 *
 * One decision covers every pending request: the agent pauses on all gated calls in a turn at
 * once, and they are approved or rejected together.
 */
export default function ToolApprovalCard({ requests, onRespond }: ToolApprovalCardProps) {
  const [mode, setMode] = useState<"idle" | "rejecting">("idle");
  const [feedback, setFeedback] = useState("");
  // The card is answered exactly once. It unmounts when the answer clears the pending request,
  // but that is a re-render away, and a second click landing before it would send the same
  // decision twice — the second resume carrying interrupt ids the runtime has already consumed.
  const [responded, setResponded] = useState(false);

  const respond = (approved: boolean) => {
    if (responded) {
      return;
    }
    setResponded(true);
    const trimmed = feedback.trim();
    onRespond(
      requests.map(request => ({
        interrupt_id: request.interrupt_id,
        approved,
        feedback: approved || !trimmed ? null : trimmed
      }))
    );
  };

  return (
    <FormBox>
      <Stack gap="sm" w="100%">
        <Stack gap={2}>
          <Text size="sm" fw={600}>
            Send this to literature search?
          </Text>
          <Text size="xs" c="dimmed">
            Everything shown below leaves the platform to search published literature, and any word in it may be used as
            a search term. Nothing else from this conversation is included.
          </Text>
        </Stack>

        {requests.map(request => (
          <Stack key={request.interrupt_id} gap="xs">
            {displayFields(request.input ?? {}).map(({ key, label, text }) => (
              <Stack key={key} gap={2}>
                <Text size="xs" fw={500} c="dimmed">
                  {label}
                </Text>
                <Code block style={{ fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {text}
                </Code>
              </Stack>
            ))}
          </Stack>
        ))}

        {mode === "rejecting" && (
          <Textarea
            value={feedback}
            onChange={event => setFeedback(event.currentTarget.value)}
            placeholder="What should change? For example: drop the site name, or narrow it to flow chemistry."
            autosize
            minRows={2}
            maxRows={6}
            size="sm"
          />
        )}

        <Group gap="xs">
          {mode === "idle" ? (
            <>
              <Button size="xs" onClick={() => respond(true)} disabled={responded}>
                Send it
              </Button>
              <Button size="xs" variant="default" onClick={() => setMode("rejecting")} disabled={responded}>
                Change something
              </Button>
            </>
          ) : (
            <>
              <Button size="xs" onClick={() => respond(false)} disabled={responded}>
                Submit
              </Button>
              <Button size="xs" variant="default" onClick={() => setMode("idle")} disabled={responded}>
                Back
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </FormBox>
  );
}
